import { FC, FormEvent, useMemo, useState } from 'react';
import s from './AssignTagsModal.module.scss';
import { Title } from '@tremor/react';
import { findIndex } from 'lodash';
import { GeneralModalProps, SelectionTag } from '@/types/modals';
import { TagFromStore } from '@/types/types';
import { TableKeyword } from '@/components/Tables/KeywordsTable/KeywordsTable';
import { ModalWrapper } from '../ModalWrapper/ModalWrapper';
import { useKeywordsStore } from '@/stores/keywordsStore';
import { KeywordsService } from '@/services/dataforseo/keywordsService';
import { SearchBar } from '../../Inputs/SearchBar/SearchBar';
import { ModalBottomActions } from '../ModalBottomActions/ModalBottomActions';
import { TagsSelection } from '../TagsSelection/TagsSelection';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';

interface Props extends GeneralModalProps {
  tags: TagFromStore[];
  editedKeyword: TableKeyword;
}

export const AssignTagsModal: FC<Props> = ({ tags, setClose, isOpen, editedKeyword }) => {
  return (
    <ModalWrapper
      className={s.assignTagsModalContainer}
      isOpen={isOpen}
      onRequestClose={setClose}
    >
      <AssignTagsModalContent
        setClose={setClose}
        tags={tags}
        editedKeyword={editedKeyword}
      />
    </ModalWrapper>
  );
};

const AssignTagsModalContent: FC<Pick<Props, 'tags' | 'setClose' | 'editedKeyword'>> = ({
  tags,
  setClose,
  editedKeyword,
}) => {
  const workspaceId = useWorkspaceIdFromParams() as string;
  const setKeywords = useKeywordsStore((state) => state.setKeywords);
  const [isLoading, setIsLoading] = useState(false);
  const [selectionTags, setSelectionTags] = useState<SelectionTag[]>(() => {
    if (editedKeyword === null) {
      return [];
    }

    return tags.map((tag) => {
      const isSelected = editedKeyword.assignedTags.some(
        (keywordTag) => keywordTag.id === tag.id,
      );
      return { ...tag, isSelected };
    });
  });
  const keywords = useKeywordsStore((state) => state.keywords);
  const [searchValue, setSearchValue] = useState('');

  const onSearchbarSubmit = (value: string) => {
    setSearchValue(value);
  };

  const displayTags = useMemo(
    () =>
      searchValue.length === 0
        ? selectionTags
        : selectionTags.filter((tag) => {
            return tag.name.toLowerCase().includes(searchValue.toLowerCase());
          }),
    [searchValue, selectionTags],
  );

  const handleTagClick = (tagId: string) => {
    const tagIndex = selectionTags.findIndex((tag) => tag.id === tagId);
    const tagCopy = { ...selectionTags[tagIndex] };
    tagCopy.isSelected = !tagCopy.isSelected;
    const tagsCopy = [...selectionTags];
    tagsCopy[tagIndex] = tagCopy;
    setSelectionTags(tagsCopy);
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    const selectedTags = selectionTags
      .filter((tag) => tag.isSelected)
      .map((tag) => tag.id);
    try {
      await KeywordsService.updateTags(
        selectedTags,
        editedKeyword.id,
        workspaceId,
        'dataforseo',
      );
      const updatedKeywordsIndex = findIndex(
        keywords,
        (keyword) => keyword.id === editedKeyword.id,
      );
      if (updatedKeywordsIndex === -1) return;
      const copiedKeywords = [...keywords];
      copiedKeywords[updatedKeywordsIndex] = {
        ...copiedKeywords[updatedKeywordsIndex],
        tags: selectedTags,
      };
      setKeywords(copiedKeywords);
      setClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className={s.modalContent}>
      <div className={s.tagsMenu}>
        <Title className={s.modalTitle}>Assign Tags</Title>
        <SearchBar placeholder="Find Tag" onSubmit={onSearchbarSubmit} />
      </div>
      <form
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        onSubmit={onSubmit}
      >
        <TagsSelection
          isLoading={isLoading}
          handleTagClick={handleTagClick}
          displayTags={displayTags}
          className={s.tagsSelectionHeightOverride}
        />
        <ModalBottomActions isLoading={isLoading} setClose={setClose} />
      </form>
    </section>
  );
};
